import React, { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Paper from '@material-ui/core/Paper';
import InputBase from '@material-ui/core/InputBase';
import Alert from '@material-ui/lab/Alert';
import DuracellOptimum from './BrandedImages/DuracellOptimum.png';
import PhishCloudBanner from '../../../Assets/Logos/FullNameLogoWhite.png';
import './BrandedLogin.css';
import EmailLoginBrandingStyles from '../../../Assets/BrandingStyles/BrandingStyles';
import validateEmail from '../../../utils/validateEmail';
import { readIntendedRoute, isSafePostLoginRoute } from '../../../auth/pcAuthBrowserFlow';

const DURACELL_DOMAIN = 'duracell.com';
const normalizeEmail = (value) => (value || '').trim().toLowerCase();

const DuracellLogin = () => {
  const history = useHistory();
  const pcAuthMicrosoftStartUrl =
    process.env.REACT_APP_PC_AUTH_MICROSOFT_START_URL || process.env.REACT_APP_PC_AUTH_START_URL || '/auth/start';

  // Read and consume validated email from sessionStorage (set by EmailLogin after Whoami check).
  // This is the only way to reach the "verified" view — no URL params to spoof.
  const [verifiedEmail] = useState(() => {
    try {
      const stored = sessionStorage.getItem('pcValidatedLoginEmail');
      if (stored) { sessionStorage.removeItem('pcValidatedLoginEmail'); }
      return stored || '';
    } catch (_) { return ''; }
  });

  const [email, setEmail] = useState(verifiedEmail);
  const [invalidDomain, setInvalidDomain] = useState(false);
  const [emailNotRegistered, setEmailNotRegistered] = useState(false);
  const [startError, setStartError] = useState('');
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isValidating, setIsValidating] = useState(false);

  const startMicrosoftLogin = useCallback(
    (rawEmail) => {
      const normalizedEmail = normalizeEmail(rawEmail);
      if (!normalizedEmail.endsWith(`@${DURACELL_DOMAIN}`)) {
        setInvalidDomain(true);
        setStartError('');
        return;
      }

      setInvalidDomain(false);
      setStartError('');

      try {
        const baseUrl = pcAuthMicrosoftStartUrl.startsWith('http')
          ? new URL(pcAuthMicrosoftStartUrl)
          : new URL(pcAuthMicrosoftStartUrl, window.location.origin);

        baseUrl.searchParams.set('provider', 'microsoft');
        baseUrl.searchParams.set('domain', DURACELL_DOMAIN);
        baseUrl.searchParams.set('email', normalizedEmail);

        // Preserve the originally-requested deep link across the SSO round-trip.
        const intendedRoute = readIntendedRoute();
        if (isSafePostLoginRoute(intendedRoute)) {
          baseUrl.searchParams.set('returnTo', intendedRoute);
        }

        setIsRedirecting(true);
        window.location.replace(baseUrl.toString());
      } catch (error) {
        setIsRedirecting(false);
        setStartError('Unable to start Microsoft sign-in. Please try again.');
      }
    },
    [pcAuthMicrosoftStartUrl]
  );

  const submitEmail = async (event) => {
    event.preventDefault();
    const normalizedEmail = normalizeEmail(email);
    if (!normalizedEmail.endsWith(`@${DURACELL_DOMAIN}`)) {
      setInvalidDomain(true);
      return;
    }
    setInvalidDomain(false);
    setEmailNotRegistered(false);
    setStartError('');
    setIsValidating(true);
    try {
      const { valid } = await validateEmail(normalizedEmail);
      if (valid) {
        startMicrosoftLogin(normalizedEmail);
      } else {
        setEmailNotRegistered(true);
      }
    } catch (error) {
      setStartError('Unable to verify email. Please try again.');
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <div
      className="parentContainer"
      style={{
        backgroundImage: `url(${DuracellOptimum})`,
        backgroundColor: '#000000',
        backgroundPosition: 'left',
        backgroundRepeat: 'no-repeat',
        width: 'auto',
      }}
    >
      <div className="container-login100">
        <Paper className="wrap-login100" style={{ backgroundColor: EmailLoginBrandingStyles.Duracell.styling.cardColor }}>
          <form className="login100-form validate-form" onSubmit={submitEmail}>
            <span className="login100-form-title p-b-26" style={{ color: '#fff' }}>
              Welcome
            </span>
            <span className="login100-form-title p-b-48">
              <img src={EmailLoginBrandingStyles.Duracell.logoDarkMode} width={100} alt="Duracell Logo" />
            </span>
            {invalidDomain && (
              <div>
                <Alert severity="error">Use your @{DURACELL_DOMAIN} email on this page.</Alert>
              </div>
            )}
            {emailNotRegistered && (
              <div>
                <Alert severity="error">This email is not registered with PhishCloud. Contact your admin if you think this is in error.</Alert>
              </div>
            )}
            {startError && (
              <div>
                <Alert severity="error">{startError}</Alert>
              </div>
            )}
            {isRedirecting && (
              <div>
                <Alert severity="info">Redirecting to Microsoft single sign-on...</Alert>
              </div>
            )}
            <br />
            {verifiedEmail ? (
              <>
                <div style={{ textAlign: 'center', margin: '16px 0', color: '#fff' }}>
                  <span style={{ fontSize: '0.9rem', opacity: 0.8 }}>Logging in as</span>
                  <br />
                  <span style={{ fontSize: '1.1rem', fontWeight: 600 }}>{verifiedEmail}</span>
                </div>
                <div className="container-login100-form-btn">
                  <div className="wrap-login100-form-btn">
                    <div className="login100-form-bgbtn"></div>
                    <button
                      type="submit"
                      className="login100-form-btn"
                      style={{ backgroundColor: EmailLoginBrandingStyles.Duracell.styling.buttonColor }}
                      disabled={isRedirecting}
                    >
                      {isRedirecting ? 'Redirecting...' : 'Sign in with Microsoft'}
                    </button>
                  </div>
                </div>
                <div style={{ textAlign: 'center', marginTop: 16 }}>
                  <button
                    type="button"
                    onClick={() => history.push('/PortalSignIn')}
                    style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', fontSize: '0.85rem', textDecoration: 'underline' }}
                  >
                    ← Back to login
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="wrap-input100 validate-input" data-validate="Valid email is: a@b.c" style={{ color: '#fff' }}>
                  <InputBase
                    className="input100"
                    type="text"
                    id="duracellFormEmail"
                    name="email"
                    placeholder={`email@${DURACELL_DOMAIN}`}
                    value={email}
                    style={{ color: '#fff' }}
                    onChange={(event) => {
                      setEmail(event.target.value);
                      setInvalidDomain(false);
                      setEmailNotRegistered(false);
                      setStartError('');
                    }}
                  />
                  <span className="focus-input100" data-placeholder="Email"></span>
                </div>
                <div className="container-login100-form-btn">
                  <div className="wrap-login100-form-btn">
                    <div className="login100-form-bgbtn"></div>
                    <button
                      type="submit"
                      className="login100-form-btn"
                      style={{ backgroundColor: EmailLoginBrandingStyles.Duracell.styling.buttonColor }}
                      disabled={isRedirecting || isValidating}
                    >
                      {isValidating ? 'Verifying...' : isRedirecting ? 'Redirecting...' : 'Continue with Microsoft'}
                    </button>
                  </div>
                </div>
              </>
            )}
            <br />
          </form>
        </Paper>
        <div className="phishCloudBanner">
          Powered by
          <br />
          <img src={PhishCloudBanner} style={{ width: '200px' }} alt="PhishCloud Banner" />
        </div>
      </div>
    </div>
  );
};

export default DuracellLogin;